/**
 * Pure helpers for dimension rendering: DimStyle resolution, measurement
 * formatting, tolerance computation, annotative context picking, subtype
 * parsing.
 *
 * Extracted from the legacy `renderDimension.tsx` SVG renderer as part
 * of Opt 2 / Phase 3b: the SVG subtype renderers have been removed (the
 * scene3d pipeline owns dimension rendering now), but the pure helpers
 * remain — they are consumed by `scene3d/dimensions/dimensionToPrimitives.ts`
 * to translate `DimensionEntity` (or legacy `EntityPreview.properties`)
 * into primitive lists.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1
 *
 * `readDimStyle` accepts both the typed `DimensionEntity` (preferred —
 * reads `styleOverrides` from the model) and the legacy string-keyed
 * `EntityPreview.properties` bag. Both fall back to `defaultDimStyle`
 * for any field not explicitly overridden, mirroring AutoCAD's
 * "use the style" semantic.
 *
 * `formatDimMeasurement` applies DIMLFAC (linear scale factor), DIMRND
 * (rounding), DIMLUNIT (unit system — Scientific / Decimal / Engineering
 * / Architectural / Fractional) and DIMPOST (prefix/suffix with `<>`
 * substitution) per the DXF DIMVAR semantics.
 *
 * `computeToleranceInfo` produces the upper/lower text and font size
 * for DIMTOL (deviation / symmetrical) and DIMLIM (limits) modes.
 *
 * `pickDimensionAnnotativeContext` resolves which per-scale context
 * snapshot (Group C — DIMANNO parity) applies for a given annotation
 * scale, with the AutoCAD fallback order (exact → 1:1 → first).
 *
 * `parseDimensionSubtype` maps the EntityPreview "Subtype" string back
 * to the DXF dimensionType integer (Linear=0, Aligned=1, Angular=2,
 * Diameter=3, Radius=4, Angular 3pt=5, Ordinate=6).
 */

import {
  applyDimRounding,
  formatDimValueByUnit,
  type DimensionEntity,
  type DimensionStyle,
} from "@dxf-ai-editor/drawing-model";
import { readNumber, readPositiveNumber } from "./renderHelpers";

/* ── Annotative context (Group C — DIMANNO parity) ─────── */

// A single per-scale snapshot of a DIMENSION's visual overrides, as
// decoded from the serialized property blob. Mirrors the MLEADER shape
// used in renderMultileader.tsx (scale + required overrides + optional
// MLEADER-specific fields that Dimension ignores).
export type EncodedDimensionAnnotativeContext = {
  scale: number;
  scaleName?: string;
  textHeight?: number;
  arrowheadSize?: number;
};

// Pick the context matching `annotationScale` (within an epsilon),
// falling back to the implicit 1:1 context, then to the first entry.
// Matches pickAnnotativeContext in renderMultileader.tsx — kept local
// to avoid a cross-renderer dependency on MLEADER-specific types.
export function pickDimensionAnnotativeContext(
  contexts: EncodedDimensionAnnotativeContext[],
  annotationScale: number | null,
): EncodedDimensionAnnotativeContext | null {
  if (contexts.length === 0 || annotationScale == null) return null;
  const epsilon = 1e-6;
  return (
    contexts.find((c) => Math.abs(c.scale - annotationScale) < epsilon) ??
    contexts.find((c) => Math.abs(c.scale - 1) < epsilon) ??
    contexts[0] ??
    null
  );
}

/* ── Dimension style helpers ───────────────────────── */

export type DimStyleValues = {
  dimasz: number;
  dimtxt: number;
  dimgap: number;
  dimexo: number;
  dimexe: number;
  dimse1: boolean;
  dimse2: boolean;
  dimclrd: number;
  dimclre: number;
  dimdec: number;
  dimpost: string;
  dimblk: string;
  dimblk1: string;
  dimblk2: string;
  dimtih: boolean;
  dimrnd: number;
  dimlfac: number;
  dimlunit: number;
  dimtol: boolean;
  dimlim: boolean;
  dimtp: number;
  dimtm: number;
  dimtfac: number;
};

/**
 * Exported so the typed DIMENSION render adapter (`resolveDimStyleFromTable`
 * below, and `scene3d/adapters/typed/dimensionFromEntity.ts`) can fall back
 * to the SAME literal defaults `readDimStyle`'s bag branch uses — one truth,
 * no drifted copy.
 */
export const defaultDimStyle: DimStyleValues = {
  dimasz: 2.5,
  dimtxt: 2.5,
  dimgap: 0.625,
  dimexo: 0.625,
  dimexe: 1.25,
  dimse1: false,
  dimse2: false,
  dimclrd: 0,
  dimclre: 0,
  dimdec: 4,
  dimpost: "",
  dimblk: "",
  dimblk1: "",
  dimblk2: "",
  dimtih: true,
  dimrnd: 0,
  dimlfac: 1,
  dimlunit: 2,
  dimtol: false,
  dimlim: false,
  dimtp: 0,
  dimtm: 0,
  dimtfac: 1,
};

/**
 * Resolve the visual DIMVAR values for a dimension. Accepts either:
 *  - a typed `DimensionEntity` (preferred — reads from
 *    `entity.styleOverrides` per Opt 2b), or
 *  - the legacy `EntityPreview.properties` string-keyed bag (kept for
 *    back-compat with consumers that still flow through the shell).
 *
 * Both paths fall back to `defaultDimStyle` for any field that is not
 * explicitly overridden — matching AutoCAD's "use the style" semantic.
 */
export function readDimStyle(
  source: Record<string, string> | DimensionEntity,
): DimStyleValues {
  // Typed path: DimensionEntity carries `styleOverrides` (Opt 2b).
  if (
    typeof source === "object" &&
    source !== null &&
    "type" in source &&
    (source as DimensionEntity).type === "Dimension"
  ) {
    const o = (source as DimensionEntity).styleOverrides ?? {};
    return {
      dimasz: o.dimasz ?? defaultDimStyle.dimasz,
      dimtxt: o.dimtxt ?? defaultDimStyle.dimtxt,
      dimgap: o.dimgap ?? defaultDimStyle.dimgap,
      dimexo: o.dimexo ?? defaultDimStyle.dimexo,
      dimexe: o.dimexe ?? defaultDimStyle.dimexe,
      dimse1: o.dimse1 ?? defaultDimStyle.dimse1,
      dimse2: o.dimse2 ?? defaultDimStyle.dimse2,
      dimclrd: o.dimclrd ?? defaultDimStyle.dimclrd,
      dimclre: o.dimclre ?? defaultDimStyle.dimclre,
      dimdec: o.dimdec ?? defaultDimStyle.dimdec,
      dimpost: o.dimpost ?? defaultDimStyle.dimpost,
      dimblk: o.dimblk ?? defaultDimStyle.dimblk,
      dimblk1: o.dimblk1 ?? defaultDimStyle.dimblk1,
      dimblk2: o.dimblk2 ?? defaultDimStyle.dimblk2,
      dimtih: o.dimtih ?? defaultDimStyle.dimtih,
      dimrnd: o.dimrnd ?? defaultDimStyle.dimrnd,
      dimlfac: o.dimlfac ?? defaultDimStyle.dimlfac,
      dimlunit: o.dimlunit ?? defaultDimStyle.dimlunit,
      dimtol: o.dimtol ?? defaultDimStyle.dimtol,
      dimlim: o.dimlim ?? defaultDimStyle.dimlim,
      dimtp: o.dimtp ?? defaultDimStyle.dimtp,
      dimtm: o.dimtm ?? defaultDimStyle.dimtm,
      dimtfac: o.dimtfac ?? defaultDimStyle.dimtfac,
    };
  }
  // Legacy path: string-keyed EntityPreview.properties bag.
  const properties = source as Record<string, string>;
  return {
    dimasz: readPositiveNumber(properties, "DIMASZ", defaultDimStyle.dimasz),
    dimtxt: readPositiveNumber(properties, "DIMTXT", defaultDimStyle.dimtxt),
    dimgap: readNumber(properties, "DIMGAP", defaultDimStyle.dimgap),
    dimexo: readNumber(properties, "DIMEXO", defaultDimStyle.dimexo),
    dimexe: readNumber(properties, "DIMEXE", defaultDimStyle.dimexe),
    dimse1: properties["DIMSE1"] === "true",
    dimse2: properties["DIMSE2"] === "true",
    dimclrd: readNumber(properties, "DIMCLRD", defaultDimStyle.dimclrd),
    dimclre: readNumber(properties, "DIMCLRE", defaultDimStyle.dimclre),
    dimdec: Math.round(readNumber(properties, "DIMDEC", defaultDimStyle.dimdec)),
    dimpost: properties["DIMPOST"] ?? defaultDimStyle.dimpost,
    dimblk: properties["DIMBLK"] ?? defaultDimStyle.dimblk,
    dimblk1: properties["DIMBLK1"] ?? defaultDimStyle.dimblk1,
    dimblk2: properties["DIMBLK2"] ?? defaultDimStyle.dimblk2,
    dimtih: properties["DIMTIH"] !== "false",
    dimrnd: readNumber(properties, "DIMRND", defaultDimStyle.dimrnd),
    dimlfac: readNumber(properties, "DIMLFAC", defaultDimStyle.dimlfac),
    dimlunit: Math.round(readNumber(properties, "DIMLUNIT", defaultDimStyle.dimlunit)),
    dimtol: properties["DIMTOL"] === "true",
    dimlim: properties["DIMLIM"] === "true",
    dimtp: readNumber(properties, "DIMTP", defaultDimStyle.dimtp),
    dimtm: readNumber(properties, "DIMTM", defaultDimStyle.dimtm),
    dimtfac: readNumber(properties, "DIMTFAC", defaultDimStyle.dimtfac),
  };
}

// `applyDimRounding`/`formatDimValueByUnit` (DIMRND rounding + DIMLUNIT/DIMDEC unit formatting)
// are now imported from `@dxf-ai-editor/drawing-model` (@codex T1 arm(c) round-22 P2 finding —
// dup:check convergence): the WCS-bounds path (`wcsBoundsLeaf.ts::formattedMeasurementText`)
// independently needed the SAME pure formatting core to size an empty/`"<>"` DIMENSION's
// measurement-text width, and jscpd flagged the two re-derivations as duplication. Both pure
// number-in/string-out functions with no further dependency, so they converge to ONE shared
// implementation here rather than two — see `dimensionMeasurementFormat.ts`'s own header.

export function formatDimMeasurement(
  rawText: string,
  measurement: number,
  style: DimStyleValues,
  prefix?: string,
): string {
  if (rawText && rawText !== "<>" && rawText !== "") {
    return rawText;
  }
  // Apply linear scale factor
  let scaledValue = measurement * style.dimlfac;
  // Apply rounding
  scaledValue = applyDimRounding(scaledValue, style.dimrnd);
  // Format by unit system
  const value = formatDimValueByUnit(scaledValue, style.dimlunit, style.dimdec);
  const prefixed = prefix ? `${prefix}${value}` : value;
  if (style.dimpost) {
    return style.dimpost.includes("<>")
      ? style.dimpost.replace("<>", prefixed)
      : `${prefixed}${style.dimpost}`;
  }
  return prefixed;
}

export type ToleranceInfo = {
  mode: "none" | "symmetrical" | "deviation" | "limits";
  upperText: string;
  lowerText: string;
  toleranceFontSize: number;
};

export function computeToleranceInfo(
  measurement: number,
  style: DimStyleValues,
): ToleranceInfo {
  const tolFontSize = style.dimtxt * style.dimtfac;

  if (style.dimlim) {
    const scaledMeasurement = measurement * style.dimlfac;
    const rounded = applyDimRounding(scaledMeasurement, style.dimrnd);
    const upper = formatDimValueByUnit(rounded + style.dimtp, style.dimlunit, style.dimdec);
    const lower = formatDimValueByUnit(rounded - style.dimtm, style.dimlunit, style.dimdec);
    return { mode: "limits", upperText: upper, lowerText: lower, toleranceFontSize: tolFontSize };
  }

  if (style.dimtol) {
    if (style.dimtp === style.dimtm) {
      const tol = formatDimValueByUnit(style.dimtp, style.dimlunit, style.dimdec);
      return { mode: "symmetrical", upperText: `±${tol}`, lowerText: "", toleranceFontSize: tolFontSize };
    }
    const upper = `+${formatDimValueByUnit(style.dimtp, style.dimlunit, style.dimdec)}`;
    const lower = `-${formatDimValueByUnit(style.dimtm, style.dimlunit, style.dimdec)}`;
    return { mode: "deviation", upperText: upper, lowerText: lower, toleranceFontSize: tolFontSize };
  }

  return { mode: "none", upperText: "", lowerText: "", toleranceFontSize: tolFontSize };
}

/* ── Dimension subtype parsing ─────────────────────── */

export function parseDimensionSubtype(subtype: string): number {
  const subtypeMap: Record<string, number> = {
    "Linear": 0,
    "Aligned": 1,
    "Angular": 2,
    "Diameter": 3,
    "Radius": 4,
    "Angular 3pt": 5,
    "Ordinate": 6,
  };
  return subtypeMap[subtype] ?? 0;
}

/* ── DIMSTYLE-table → DimStyleValues resolution ─────────────────────── */

/** `value` when it is a finite POSITIVE number, else `fallback`. Local
 *  scene-layer copy of `scene3d/adapters/adapterContext.ts::positiveOr`
 *  (that module cannot be imported from this PURE scene-layer helper without
 *  adding a scene→scene3d edge / dragging its WebGL deps in). */
function positiveOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

/**
 * Resolve the named DIMSTYLE table ROW for a dimension (`styleName`, falling
 * back to "Standard"). Relocated here (codex PR #472 round-4 finding 4) from
 * the three.js-laden `scene3d/adapters/geom/dimensionGeometry.ts` (which now
 * RE-EXPORTS it for its existing importers) so BOTH the typed render adapter
 * (`dimensionFromEntity.ts`) AND the PURE P7 insertion-overlap bounds path
 * (`scene/insertionOverlap.ts`) share ONE truth for DIMSTYLE resolution
 * without a pure geometry util importing a WebGL mesh module.
 */
export function resolveDimensionStyleRow(
  styleName: string,
  dimStylesByName: ReadonlyMap<string, DimensionStyle>,
): DimensionStyle | undefined {
  return dimStylesByName.get(styleName) ?? dimStylesByName.get("Standard");
}

/**
 * Map a DIMSTYLE table ROW into the resolved `DimStyleValues` the renderer +
 * bounds paths consume — the direct typed counterpart of the legacy
 * `resolveDimVarProperties` (`state/dxfGeometry.ts`).
 *
 * # Golden-parity note (NOT `readDimStyle`'s typed branch)
 *
 * `readDimStyle`'s typed branch reads ONLY `entity.styleOverrides` (the
 * per-entity DSTYLE XDATA delta) falling back straight to hardcoded literals;
 * it never consults the named DIMSTYLE TABLE. The live render path does the
 * OPPOSITE — resolve DIMVARs from `dimStylesByName.get(styleName) ??
 * .get("Standard")`. This function is that table resolution; each field falls
 * back to `defaultDimStyle` for a non-finite table value (mirroring
 * `readDimStyle`'s bag-branch clamps).
 */
export function dimensionStyleRowToValues(
  style: DimensionStyle | undefined,
): DimStyleValues {
  if (!style) return { ...defaultDimStyle };
  const numOr = (v: number, fallback: number): number =>
    Number.isFinite(v) ? v : fallback;
  return {
    dimasz: positiveOr(style.DIMASZ, defaultDimStyle.dimasz),
    dimtxt: positiveOr(style.DIMTXT, defaultDimStyle.dimtxt),
    dimgap: numOr(style.DIMGAP, defaultDimStyle.dimgap),
    dimexo: numOr(style.DIMEXO, defaultDimStyle.dimexo),
    dimexe: numOr(style.DIMEXE, defaultDimStyle.dimexe),
    dimse1: style.DIMSE1,
    dimse2: style.DIMSE2,
    dimclrd: numOr(style.DIMCLRD, defaultDimStyle.dimclrd),
    dimclre: numOr(style.DIMCLRE, defaultDimStyle.dimclre),
    dimdec: Math.round(numOr(style.DIMDEC, defaultDimStyle.dimdec)),
    dimpost: style.DIMPOST,
    dimblk: style.DIMBLK,
    dimblk1: style.DIMBLK1,
    dimblk2: style.DIMBLK2,
    dimtih: style.DIMTIH,
    dimrnd: numOr(style.DIMRND, defaultDimStyle.dimrnd),
    dimlfac: numOr(style.DIMLFAC, defaultDimStyle.dimlfac),
    dimlunit: Math.round(numOr(style.DIMLUNIT, defaultDimStyle.dimlunit)),
    dimtol: style.DIMTOL,
    dimlim: style.DIMLIM,
    dimtp: numOr(style.DIMTP, defaultDimStyle.dimtp),
    dimtm: numOr(style.DIMTM, defaultDimStyle.dimtm),
    dimtfac: numOr(style.DIMTFAC, defaultDimStyle.dimtfac),
  };
}

/**
 * Fold ONE DIMENSION entity's referenced DIMSTYLE-table values into its typed
 * `styleOverrides` for the four DIMVARs cad-core's `dimensionBounds` reads
 * (DIMEXE / DIMEXO / DIMGAP / DIMTXT) — the single-truth per-entity resolver
 * shared by BOTH overlap-footprint sites (codex PR #472 round-4 finding 4 +
 * round-5 finding 1):
 *
 *   - top-level existing entities — `insertionOverlap.ts::
 *     resolveDimensionStyleBounds` maps this over the target space's entities;
 *   - DIMENSION children nested INSIDE a block definition (the inserted block
 *     itself, or an existing Block INSERT resolved through the raw
 *     `geometryDocument.blocks`) — `blockInstanceResolve.ts::
 *     specializeDefinition` applies this while it rewrites the per-instance
 *     specialized table, so a dimension inside a block resolves the SAME
 *     style-driven extension / text padding as a top-level one instead of
 *     reaching cad-core with EMPTY `styleOverrides` and falling to hardcoded
 *     defaults.
 *
 * cad-core's `dimensionBounds` reads `e.styleOverrides.dimexe ?? 1.25` (etc.):
 * a per-entity DSTYLE override wins, but when ABSENT it falls to a HARDCODED
 * default — it never consults the named DIMSTYLE table. Materialising the
 * EFFECTIVE (per-entity override ?? DIMSTYLE-table) value onto `styleOverrides`
 * makes the raw typed entity carry the same style enrichment the render path
 * applies. `dimtxt` mirrors the renderer's
 * `positiveOr(textHeight, style.dimtxt)` effective-height rule (per-entity
 * override ?? `entity.textHeight`-when-positive ?? table DIMTXT).
 *
 * Non-DIMENSION entities pass through BY REFERENCE (never copied); DIMENSION
 * entities are copied (never mutated). With no matching style the resolution
 * yields `defaultDimStyle`, whose four values EQUAL cad-core's hardcoded
 * fallbacks (DIMEXE 1.25 / DIMEXO 0.625 / DIMGAP 0.625 / DIMTXT 2.5), so a
 * document with no enriching style is byte-identical to the un-resolved
 * behaviour (no regression).
 */
export function applyDimensionStyleOverrides<E extends { type: string }>(
  entity: E,
  dimStylesByName: ReadonlyMap<string, DimensionStyle>,
): E {
  if (entity.type !== "Dimension") return entity;
  const dim = entity as unknown as DimensionEntity;
  const resolved = dimensionStyleRowToValues(
    resolveDimensionStyleRow(dim.styleName ?? "Standard", dimStylesByName),
  );
  const o = dim.styleOverrides ?? {};
  return {
    ...entity,
    styleOverrides: {
      ...o,
      dimexe: o.dimexe ?? resolved.dimexe,
      dimexo: o.dimexo ?? resolved.dimexo,
      dimgap: o.dimgap ?? resolved.dimgap,
      dimtxt:
        o.dimtxt ?? (dim.textHeight > 0 ? dim.textHeight : resolved.dimtxt),
    },
  } as E;
}
